import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  PlusIcon,
  DocumentIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  PhotoIcon,
  CheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

import ImageCropper from 'components/ImageCropper';
import { useImageCropper } from 'hooks/useImageCropper';
import { useAlert } from 'context/AlertContext';
import {
  validateFiles,
  IMAGE_ACCEPT,
  DOCUMENT_ACCEPT,
} from 'utils/uploadValidation';

import { UTILITIES, getUtilitiesLabels } from 'utils/utilities';

import {
  StepShell,
  HelpedBlock,
  FormCard,
  Button,
} from '../atoms';

const MAX_ADDITIONAL_IMAGES = 6;

const normalize = (str) =>
  String(str || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const slugify = (str) =>
  normalize(str)
    .replace(/[^a-z0-9\s_-]+/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');

const Step2 = ({
  methods,
  eventIcon,
  landscapeImage,
  additionalImages,
  regulatoryDocument,
  handleSingleImageUpload,
  handleAdditionalImagesUpload,
  handleRegulatoryUpload,
  clearSingleImage,
  removeAdditionalImage,
  clearRegulatoryUpload,
}) => {
  const {
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = methods;

  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();
  const iconCrop = useImageCropper();

  // Validate before handing the file to the cropper: avoids loading a huge or
  // unsupported file into the FileReader and surfaces the limit right away.
  const onIconFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const { accepted, errors: uploadErrors } = validateFiles(files, {
      kind: 'image',
      t,
    });
    if (uploadErrors.length) {
      showAlert({
        type: 'warning',
        title: t('upload.error_title', { defaultValue: 'Upload issue' }),
        message: uploadErrors.join('\n'),
        primaryAction: { label: t('common.ok') },
      });
      e.target.value = '';
      return;
    }
    if (accepted.length) iconCrop.handleImageChange(e);
  };

  const [newAmenity, setNewAmenity] = useState('');
  const [warning, setWarning] = useState('');
  const [newDocument, setNewDocument] = useState('');
  const [docWarning, setDocWarning] = useState('');

  const officialOptions = useMemo(() => {
    const lang = i18n?.language || 'en';
    return getUtilitiesLabels(lang);
  }, [i18n?.language]);

  const additionalCount = additionalImages?.length ?? 0;
  const remainingAdditional = Math.max(
    0,
    MAX_ADDITIONAL_IMAGES - additionalCount
  );
  const additionalLimitReached = remainingAdditional === 0;

  const onAdditionalImagesChange = (e) => {
    if (additionalLimitReached) return;

    const files = Array.from(e.target.files || []);
    if (files.length > remainingAdditional) {
      const dt = new DataTransfer();
      files.slice(0, remainingAdditional).forEach((f) => dt.items.add(f));
      e.target.files = dt.files;
    }

    handleAdditionalImagesUpload(e);
    e.target.value = '';
  };

  const officialLabelsNormalized = useMemo(() => {
    const base = new Set();
    for (const u of UTILITIES) {
      base.add(normalize(u.labels.en));
      base.add(normalize(u.labels.pt));

      if (u.synonyms?.en) u.synonyms.en.forEach((s) => base.add(normalize(s)));
      if (u.synonyms?.pt) u.synonyms.pt.forEach((s) => base.add(normalize(s)));
    }
    officialOptions.forEach((o) => base.add(normalize(o.label)));
    return base;
  }, [officialOptions]);

  const requiredDocumentsRaw = watch('requiredDocuments');
  const requiredDocuments = useMemo(
    () => requiredDocumentsRaw || [],
    [requiredDocumentsRaw]
  );

  const handleAddDocument = () => {
    const raw = newDocument.trim();
    if (!raw) return;
    const normalized = normalize(raw);
    if (requiredDocuments.map(normalize).includes(normalized)) {
      setDocWarning(t('event_create.required_doc_duplicate'));
      return;
    }
    setValue('requiredDocuments', [...requiredDocuments, raw], {
      shouldDirty: true,
    });
    setNewDocument('');
    setDocWarning('');
  };

  const removeRequiredDocument = (idx) => {
    setValue(
      'requiredDocuments',
      requiredDocuments.filter((_, i) => i !== idx),
      { shouldDirty: true }
    );
  };

  const customAmenitiesRaw = watch('customAmenities');
  const customAmenities = useMemo(
    () => customAmenitiesRaw || [],
    [customAmenitiesRaw]
  );
  const selectedUtilities = watch('utilities') || [];

  const renderOptions = useMemo(() => {
    const customs = (customAmenities || []).map((label) => ({
      id: `custom:${slugify(label)}`,
      label,
      isCustom: true,
    }));

    return [
      ...officialOptions.map((o) => ({ ...o, isCustom: false })),
      ...customs,
    ];
  }, [officialOptions, customAmenities]);

  const removeImage = (id, type) => {
    if (type === 'icon') return clearSingleImage?.('icon');
    if (type === 'main') return clearSingleImage?.('main');
    if (type === 'additional') return removeAdditionalImage?.(id);
  };

  const handleIconCropSave = async (output) => {
    await iconCrop.handleCropSave(output);

    const blob =
      output instanceof Blob
        ? output
        : iconCrop.croppedImage || (await iconCrop.normalizeToBlob?.(output));

    if (blob) {
      const file = new File([blob], 'event-icon.jpg', {
        type: blob.type || 'image/jpeg',
      });
      const syntheticEvent = { target: { files: [file] } };
      handleSingleImageUpload(syntheticEvent, 'icon');
    }
  };

  const removeRegulatory = () => clearRegulatoryUpload?.();

  const handleAddAmenity = () => {
    setWarning('');
    const raw = newAmenity.trim();
    if (!raw) return;

    const normalized = normalize(raw);
    const customId = `custom:${slugify(raw)}`;

    if (officialLabelsNormalized.has(normalized)) {
      setWarning(t('event_create.utilities_warning_exists'));
      return;
    }

    const customNormalizedSet = new Set((customAmenities || []).map(normalize));
    if (customNormalizedSet.has(normalized)) {
      setWarning(t('event_create.utilities_warning_custom_duplicate'));
      return;
    }

    const updatedCustoms = [...customAmenities, raw];
    setValue('customAmenities', updatedCustoms, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const currentUtilities = getValues('utilities') || [];
    if (!currentUtilities.includes(customId)) {
      setValue('utilities', [...currentUtilities, customId], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    setNewAmenity('');
  };

  const isChecked = (id) => (selectedUtilities || []).includes(id);

  return (
    <StepShell
      eyebrow={t('event_create.step2_eyebrow', 'Step 02 / Media & amenities')}
      title={t(
        'event_create.step2_title',
        'How does it look, what do you provide?'
      )}
      lede={t(
        'event_create.step2_lede',
        'Brands skim photos before reading anything. A great cover image and a clear list of amenities convert.'
      )}
    >
      {/* Visuals */}
      <HelpedBlock sectionKey="visuals">
      <FormCard title={t('event_create.visuals', 'Visuals')}>
        <div className="grid grid-cols-12 gap-5">
          {/* Cover (landscape) */}
          <div className="col-span-12 md:col-span-8">
            <span className="field-label block mb-2">
              {t(
                'event_create.cover_label',
                'Cover image · 16:9 · used everywhere'
              )}
            </span>

            <input
              key={landscapeImage ? 'has-main' : 'no-main'}
              type="file"
              accept={IMAGE_ACCEPT}
              id="main-image-upload"
              className="sr-only"
              onChange={(e) => handleSingleImageUpload(e, 'main')}
            />

            {landscapeImage ? (
              <div className="relative rounded-xl overflow-hidden border border-line">
                <img
                  src={landscapeImage.preview}
                  alt="cover"
                  className="w-full aspect-[16/9] object-cover"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    removeImage(null, 'main');
                  }}
                  className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-white/95 border border-line shadow-card text-gray-500 hover:text-rose-600"
                  aria-label="remove cover"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
                <label
                  htmlFor="main-image-upload"
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-line shadow-card text-[12.5px] font-bold text-ink-strong hover:bg-paper-2"
                >
                  {t('common.change', 'Change')}
                </label>
              </div>
            ) : (
              <label
                htmlFor="main-image-upload"
                className="relative block cursor-pointer"
              >
                <div className="placeholder rounded-xl border border-line aspect-[16/9] grid place-items-center">
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-line shadow-card text-[12.5px] font-bold text-ink-strong hover:bg-paper-2">
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    {t('event_create.upload_cover', 'Upload cover')}
                  </span>
                </div>
              </label>
            )}
            <p className="field-helper mt-2">
              {t(
                'event_create.cover_helper',
                'JPG or PNG, at least 1600×900. Replaces previous cover.'
              )}
            </p>
          </div>

          {/* Icon */}
          <div className="col-span-12 md:col-span-4">
            <span className="field-label block mb-2">
              {t('event_create.icon_label', 'Event icon · 1:1 · for cards')}
            </span>

            <input
              key={eventIcon ? 'has-icon' : 'no-icon'}
              type="file"
              accept={IMAGE_ACCEPT}
              id="event-icon-upload"
              className="sr-only"
              onChange={onIconFileChange}
            />

            <div className="flex flex-col items-center gap-3">
              {eventIcon ? (
                <div className="relative">
                  <img
                    src={eventIcon.preview}
                    alt="icon"
                    className="w-32 h-32 object-cover rounded-xl border border-line"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      removeImage(null, 'icon');
                    }}
                    className="absolute -top-2 -right-2 w-7 h-7 grid place-items-center rounded-full bg-white border border-line shadow-card text-gray-500 hover:text-rose-600"
                    aria-label="remove icon"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                  <label
                    htmlFor="event-icon-upload"
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 cursor-pointer px-3 py-1.5 rounded-full bg-white border border-line shadow-card text-[11px] font-bold text-ink-strong whitespace-nowrap hover:bg-paper-2"
                  >
                    {t('common.change', 'Replace')}
                  </label>
                </div>
              ) : (
                <label htmlFor="event-icon-upload" className="cursor-pointer">
                  <div className="placeholder w-32 h-32 rounded-xl border border-line grid place-items-center">
                    <PhotoIcon className="w-6 h-6 text-gray-500" />
                  </div>
                </label>
              )}
              <p className="field-helper text-center">
                {t(
                  'event_create.icon_helper',
                  'Square crop, 512px+. We round it on cards.'
                )}
              </p>
            </div>

            {iconCrop.isCropping && iconCrop.imagePreview && (
              <ImageCropper
                imageSrc={iconCrop.imagePreview}
                aspect={1}
                shape="rect"
                onCropSave={handleIconCropSave}
                onClose={iconCrop.resetImage}
                cornerRadiusPx={16}
              />
            )}
          </div>
        </div>

        {/* Gallery */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="field-label">
              {t('event_create.gallery_label', 'Gallery · up to 6')}
            </span>
            <span className="text-[11px] font-semibold text-muted tabular-nums">
              {additionalCount}/{MAX_ADDITIONAL_IMAGES}
            </span>
          </div>

          <input
            type="file"
            multiple
            accept={IMAGE_ACCEPT}
            id="additional-images-upload"
            className="sr-only"
            onChange={onAdditionalImagesChange}
            disabled={additionalLimitReached}
          />

          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {additionalImages?.map((image, i) => (
              <div key={image.id} className="relative">
                <img
                  src={image.preview}
                  alt={`gallery ${i + 1}`}
                  className="w-full aspect-square object-cover rounded-xl border border-line"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    removeImage(image.id, 'additional');
                  }}
                  className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-white border border-line text-gray-500 hover:text-rose-600 shadow-sm"
                  aria-label="remove image"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
            {!additionalLimitReached && (
              <label
                htmlFor="additional-images-upload"
                className="aspect-square rounded-xl border border-dashed border-line bg-paper-2 hover:bg-paper text-gray-500 hover:text-ink-strong grid place-items-center transition cursor-pointer"
              >
                <span className="flex flex-col items-center gap-1">
                  <PlusIcon className="w-4 h-4" />
                  <span className="text-[10px] font-bold uppercase tracking-widest">
                    {t('common.add', 'Add')}
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>
      </FormCard>

      </HelpedBlock>

      {/* Regulatory document */}
      <HelpedBlock sectionKey="regulatory">
      <FormCard
        title={t('event_create.regulatory_document_card', 'Regulatory document')}
        action={
          <span className="text-[11px] text-muted font-semibold">
            {t('event_create.optional_pdf', 'Optional · PDF')}
          </span>
        }
      >
        <p className="-mt-1 mb-4 text-[12.5px] text-gray-500">
          {t(
            'event_create.regulatory_lede',
            'If your venue has a rules document brands must agree to (insurance, conduct, fire safety), upload it here.'
          )}
        </p>

        <input
          type="file"
          accept={DOCUMENT_ACCEPT}
          id="regulatory-upload"
          className="sr-only"
          onChange={handleRegulatoryUpload}
        />

        {regulatoryDocument || watch('regulatoryName') ? (
          <div className="flex items-center gap-3 p-3 rounded-xl border border-line bg-paper-2">
            <span className="w-10 h-10 grid place-items-center rounded-lg bg-rose-50 text-rose-600 shrink-0">
              <DocumentIcon className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-ink-strong truncate">
                {regulatoryDocument?.name ||
                  watch('regulatoryName') ||
                  t('event_create.regulatory_document')}
              </p>
              <p className="text-[11px] text-muted">
                {t('event_create.uploaded', 'Uploaded')}
              </p>
            </div>
            <label
              htmlFor="regulatory-upload"
              className="cursor-pointer h-8 px-3 inline-flex items-center text-[12px] font-bold rounded-full bg-white border border-line hover:bg-paper-2 text-ink-strong"
            >
              {t('common.change', 'Replace')}
            </label>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                removeRegulatory();
              }}
              className="w-9 h-9 grid place-items-center rounded-full text-gray-400 hover:bg-rose-50 hover:text-rose-600"
              aria-label="remove regulatory"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label
            htmlFor="regulatory-upload"
            className="block w-full py-8 rounded-xl border border-dashed border-line bg-paper-2 hover:bg-paper grid place-items-center transition cursor-pointer"
          >
            <span className="flex flex-col items-center gap-2 text-gray-500">
              <ArrowUpTrayIcon className="w-5 h-5" />
              <span className="text-[12.5px] font-bold text-ink-strong">
                {t(
                  'event_create.regulatory_drop',
                  'Drop a PDF here, or click to browse'
                )}
              </span>
              <span className="text-[11px] text-muted">
                {t('event_create.regulatory_max', 'Max 10 MB')}
              </span>
            </span>
          </label>
        )}
      </FormCard>
      </HelpedBlock>

      {/* Required documents */}
      <HelpedBlock sectionKey="requiredDocuments">
      <FormCard
        title={t(
          'event_create.required_documents_card',
          'Documents brands must provide'
        )}
      >
        <p className="-mt-1 mb-4 text-[12.5px] text-gray-500">
          {t('event_create.required_documents_description')}
        </p>

        {requiredDocuments.length > 0 && (
          <ul className="space-y-2 mb-4">
            {requiredDocuments.map((doc, idx) => (
              <li
                key={idx}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-line bg-white"
              >
                <span className="w-7 h-7 grid place-items-center rounded-lg bg-brand-soft text-amber-700 shrink-0">
                  <DocumentTextIcon className="w-4 h-4" />
                </span>
                <span className="flex-1 text-[13px] font-semibold text-ink-strong">
                  {doc}
                </span>
                <button
                  type="button"
                  onClick={() => removeRequiredDocument(idx)}
                  className="w-8 h-8 grid place-items-center rounded-full text-gray-400 hover:bg-rose-50 hover:text-rose-600"
                  aria-label={`remove ${doc}`}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newDocument}
            onChange={(e) => {
              setNewDocument(e.target.value);
              if (docWarning) setDocWarning('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddDocument();
              }
            }}
            className="field-input flex-1"
            placeholder={t('event_create.required_document_placeholder')}
          />
          <Button
            variant="primary"
            type="button"
            onClick={handleAddDocument}
            icon={<PlusIcon className="w-4 h-4" />}
          >
            {t('common.add', 'Add')}
          </Button>
        </div>

        {!!docWarning && (
          <div className="mt-3 flex items-start gap-2 text-amber-800 bg-brand-softer border border-amber-200 rounded-xl p-3">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5" />
            <p className="text-[12.5px]">{docWarning}</p>
          </div>
        )}
      </FormCard>
      </HelpedBlock>

      {/* Amenities */}
      <HelpedBlock sectionKey="amenities">
      <FormCard
        title={t('event_create.amenities_card', 'What you provide on-site')}
        action={
          <span className="text-[11px] font-semibold text-muted">
            {selectedUtilities.length} {t('event_create.selected', 'selected')}
          </span>
        }
      >
        <p className="-mt-1 mb-4 text-[12.5px] text-gray-500">
          {t('event_create.amenities_description')}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {renderOptions.map((opt, index) => {
            const inputId = `amenity-${index}`;
            const checked = isChecked(opt.id);
            return (
              <div key={opt.id + index} className="relative">
                <input
                  id={inputId}
                  type="checkbox"
                  {...register('utilities')}
                  value={opt.id}
                  defaultChecked={checked}
                  className="sr-only peer"
                />
                <label
                  htmlFor={inputId}
                  className={[
                    'flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition cursor-pointer text-left select-none',
                    'bg-white border-line text-gray-600 hover:bg-paper-2',
                    'peer-checked:bg-brand-soft peer-checked:border-brand peer-checked:text-ink-strong peer-checked:font-bold',
                    opt.isCustom
                      ? 'peer-checked:bg-brand-softer peer-checked:border-amber-300'
                      : '',
                  ].join(' ')}
                  title={opt.label}
                >
                  <span className="text-[18px] leading-none shrink-0">
                    {opt.isCustom ? '✨' : '•'}
                  </span>
                  <span className="text-[12.5px] font-bold flex-1 line-clamp-2 break-words leading-tight">
                    {opt.label}
                    {opt.isCustom && (
                      <span className="block text-[9px] tracking-widest text-amber-700 uppercase font-bold">
                        {t('common.custom', 'Custom')}
                      </span>
                    )}
                  </span>
                  <span className="w-4 h-4 rounded-full bg-ink-strong text-brand grid place-items-center peer-checked:opacity-100 opacity-0 shrink-0">
                    <CheckIcon className="w-3 h-3" />
                  </span>
                </label>
              </div>
            );
          })}
        </div>

        {errors.utilities && (
          <p className="text-rose-600 text-[12px] mt-2">
            {errors.utilities.message}
          </p>
        )}

        {/* Add custom amenity */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newAmenity}
            onChange={(e) => {
              setNewAmenity(e.target.value);
              if (warning) setWarning('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddAmenity();
              }
            }}
            className="field-input flex-1"
            placeholder={t(
              'event_create.custom_amenity_placeholder',
              'Add a custom amenity (e.g. Bike racks, Fitting rooms)…'
            )}
          />
          <Button
            variant="outline"
            type="button"
            onClick={handleAddAmenity}
            icon={<PlusIcon className="w-4 h-4" />}
          >
            {t('common.add', 'Add')}
          </Button>
        </div>

        {!!warning && (
          <div className="mt-3 flex items-start gap-2 text-amber-800 bg-brand-softer border border-amber-200 rounded-xl p-3">
            <ExclamationTriangleIcon className="w-4 h-4 mt-0.5" />
            <p className="text-[12.5px]">{warning}</p>
          </div>
        )}
      </FormCard>
      </HelpedBlock>
    </StepShell>
  );
};

export default Step2;
